import {
  Heading,
  IconButton,
  Notice,
  Select,
  Shadow,
  Text,
  Stack,
  VStack,
} from '@genial/components';
import { Utils } from '@genial/utils';
import { Box, useTheme } from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';

import { LateralBar } from '../../../../../../../../../../../../components/LateralBar';
import { PrivatePensionEntityNormalized } from '../../../../../../../../../../../../store/product/types';
import { PrivatePensionAssetController } from '../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionPaymentMethod } from '../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';

export namespace AccountHolderAndPaymentCardTypes {
  export interface Props {
    offer: PrivatePensionEntityNormalized;
    offerController: PrivatePensionAssetController;
    onEdit: () => void;
  }
}

export const AccountHolderAndPaymentCard: React.FC<AccountHolderAndPaymentCardTypes.Props> = ({
  offerController,
  onEdit,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const theme = useTheme();
  const gap = Utils.Token.ToInt(theme.sizes, 'stack.xxxs');

  const {
    accountHolder,
    bankAccountOptions,
    dueDateOptions,
    isDueDateEnabled,
    paymentMethodOptions,
    accountHolderForm,
    customerLoading,
    setPaymentMethod,
    setDueDate,
    setBankAccountId,
  } = offerController;

  const {
    setValue,
    formState: { errors },
    getValues,
  } = accountHolderForm;

  const {
    paymentMethod: formPaymentMethod,
    dueDate: formDueDate,
    bankAccountId: formBankAccountId,
  } = getValues();

  const isDebit = formPaymentMethod === PrivatePensionPaymentMethod.Debit;
  const showDebitUnavailableNotice = isDebit && !bankAccountOptions.length;

  const items = useMemo(() => {
    return [
      {
        label: 'Nome completo',
        value: accountHolder?.name,
        isLoading: customerLoading,
      },
      {
        label: 'CPF',
        value: accountHolder?.cpf,
        isLoading: customerLoading,
      },
      {
        label: 'E-mail',
        value: accountHolder?.email,
        isLoading: customerLoading,
      },
    ];
  }, [accountHolder, customerLoading]);

  const handlePaymentMethodChange = useCallback(
    (newValue: string) => {
      const newPaymentMethod = Number(newValue) as unknown as PrivatePensionPaymentMethod;

      setValue('paymentMethod', newPaymentMethod, {
        shouldValidate: true,
      });
      setPaymentMethod(newPaymentMethod);
    },
    [setPaymentMethod, setValue]
  );

  const handleDueDateChange = useCallback(
    (newValue: string) => {
      const newDueDate = Number(newValue);

      setValue('dueDate', newDueDate, {
        shouldValidate: true,
      });
      setDueDate(newDueDate);
    },
    [setDueDate, setValue]
  );

  const handleBankChange = useCallback(
    (newValue: string) => {
      const newBankAccountId = Number(newValue);

      setValue('bankAccountId', newBankAccountId, {
        shouldValidate: true,
      });
      setBankAccountId(newBankAccountId);
    },
    [setBankAccountId, setValue]
  );

  const bankAccountFlexBasis = useMemo(
    () => ({ base: '100%', sm: '100%', lg: `${containerWidth / 3 - gap}px` }),
    [containerWidth, gap]
  );

  const otherFieldsFlexBasis = useMemo(
    () => ({
      base: '100%',
      sm: `${containerWidth / 2 - gap}px`,
      lg: `${containerWidth / 3 - gap}px`,
    }),
    [containerWidth, gap]
  );

  const bankAccountControl = useMemo(() => {
    if (!isDebit) {
      return null;
    }

    if (showDebitUnavailableNotice) {
      return (
        <Box flex={1} flexBasis={bankAccountFlexBasis}>
          <Notice
            textObjects={[
              { text: 'Atenção: ', variant: 'bold' },
              {
                text: 'Este cliente não possui uma conta cadastrada. Selecione outra forma de pagamento, ou altere o responsável financeiro.',
                variant: 'regular',
              },
            ]}
            type="warning"
          />
        </Box>
      );
    }

    return (
      <VStack space="stack.quarck" flex={1} flexBasis={bankAccountFlexBasis}>
        <Text variant="semibold">Banco</Text>
        <Select
          placeholder="Selecione"
          options={bankAccountOptions}
          maxOptionPerRender={5}
          onValueChange={handleBankChange}
          selectedValue={formBankAccountId?.toString()}
          error={errors.bankAccountId}
        />
      </VStack>
    );
  }, [
    bankAccountFlexBasis,
    bankAccountOptions,
    errors.bankAccountId,
    formBankAccountId,
    handleBankChange,
    isDebit,
    showDebitUnavailableNotice,
  ]);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  return (
    <Shadow level={1} borderRadius="sm">
      <LateralBar barColor="product.previdencia" />
      <VStack padding="inset.sm" space="stack.xxxs">
        <Heading size="h3" testId="account-holder">
          Responsável Financeiro
        </Heading>
        <Stack direction={{ base: 'column', md: 'row' }} width="100%" space="stack.xxxs">
          {items.map((item) => (
            <VStack key={item.label} flex={1} space="stack.quarck">
              <Text variant="semibold">{item.label}</Text>
              <Text isLoading={item.isLoading}>{item.value}</Text>
            </VStack>
          ))}
          <IconButton
            icon="Edit"
            onPress={onEdit}
            testId="edit-account-holder"
            iconColor="brand.tertiary.principal"
            iconSize="xs"
          />
        </Stack>
        <Box borderBottomWidth={1} borderBottomColor="neutral.lightest" />
        <Heading size="h3" testId="payment">
          Pagamento
        </Heading>
        <Box
          flexDirection="row"
          style={{ gap }}
          flexWrap="wrap"
          width="100%"
          onLayout={handleLayout}>
          <VStack space="stack.quarck" flex={1} flexBasis={otherFieldsFlexBasis}>
            <Text variant="semibold">Forma de pagamento</Text>
            <Select
              placeholder="Selecione"
              onValueChange={handlePaymentMethodChange}
              selectedValue={formPaymentMethod?.toString()}
              options={paymentMethodOptions}
              error={errors.paymentMethod}
            />
          </VStack>
          {bankAccountControl}
          <VStack space="stack.quarck" flex={1} flexBasis={otherFieldsFlexBasis}>
            <Text variant="semibold">Data de vencimento</Text>
            <Select
              placeholder="Selecione"
              options={dueDateOptions}
              maxOptionPerRender={5}
              onValueChange={handleDueDateChange}
              selectedValue={formDueDate?.toString()}
              error={errors.dueDate}
              disabled={!isDueDateEnabled}
            />
          </VStack>
        </Box>
      </VStack>
    </Shadow>
  );
};
